import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useLocale } from '../context';

const CACHE_TTL_MS = 60_000;
let cachedBadges = null;
let cacheLoadedAt = 0;
let cachePromise = null;
let pollStarted = false;
const listeners = new Set();

function notify(list) {
  listeners.forEach((fn) => {
    try { fn(list); } catch {}
  });
}

async function loadBadges(force = false) {
  const stale = !cachedBadges || Date.now() - cacheLoadedAt > CACHE_TTL_MS;
  if (!force && !stale && cachedBadges) return cachedBadges;
  if (cachePromise && !force) return cachePromise;

  cachePromise = api.casinoBadges()
    .then((result) => {
      cachedBadges = result.badges || [];
      cacheLoadedAt = Date.now();
      notify(cachedBadges);
      return cachedBadges;
    })
    .catch(() => {
      cacheLoadedAt = Date.now();
      return cachedBadges || [];
    })
    .finally(() => {
      cachePromise = null;
    });

  return cachePromise;
}

function ensureSharedPoller() {
  if (pollStarted || typeof window === 'undefined') return;
  pollStarted = true;
  window.setInterval(() => {
    loadBadges(true);
  }, CACHE_TTL_MS);
}

export function useCasinoBadges() {
  const { locale } = useLocale();
  const [badges, setBadges] = useState(cachedBadges || []);
  const [loading, setLoading] = useState(!cachedBadges);

  useEffect(() => {
    let alive = true;
    const onUpdate = (list) => {
      if (alive) {
        setBadges(list);
        setLoading(false);
      }
    };
    listeners.add(onUpdate);
    ensureSharedPoller();
    loadBadges().then((list) => {
      if (alive) {
        setBadges(list);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
      listeners.delete(onUpdate);
    };
  }, []);

  const map = useMemo(() => {
    const byKey = {};
    for (const badge of badges) {
      byKey[badge.key] = {
        label: locale === 'en' ? badge.label_en : badge.label_tr,
        color: badge.color,
      };
    }
    return byKey;
  }, [badges, locale]);

  const resolveBadgeDisplay = (key) => map[key] || null;

  return { badges, map, loading, resolveBadgeDisplay };
}
